import isEmpty from 'lodash-es/isEmpty'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { gameFromPGN, pgnParseHeaders } from '../../chess/pgn'
import { GameTree } from '../../chess/types'
import { API_URL } from '../../env'
import { Dialogs, useDialogs } from '../../sharedComponents/src/Contexts/DialogsContext'
import ForgotPassword from '../../sharedComponents/src/Dialogs/Forms/ForgotPassword'
import ResetPassword from '../../sharedComponents/src/Dialogs/Forms/ResetPassword'
import SignIn from '../../sharedComponents/src/Dialogs/Forms/SignIn'
import SignUp from '../../sharedComponents/src/Dialogs/Forms/SignUp'
import { useConfig } from '../../sharedComponents/src/globalHeader/services/AppConfigService'
import SettingsDialog from '../../sharedComponents/src/settings/SettingsDialog'
import { useStoreActions, useStoreState } from '../../store/hooks'
import { GameViewState } from '../gameView/GameViewModel'

enum DeepLinkTypes {
    SYSTEM = 'system',
    DRAWER = 'drawer',
    PGN = 'pgn',
    OVERLAY = 'overlay',
    RESET_PASSWORD = 'resetPassword',
    TOKEN = 'token',
}

enum DrawerTypes {
    GAME = 'game',
    PLAYERS = 'players',
    BOTS = 'bots',
    MENU = 'menu',
}

const deepLinkTypesValues = Object.values(DeepLinkTypes)

const DeepLinksService: React.FC = () => {
    const activeTab = useStoreState((state) => state.activeTab)
    const isDrawerMenuOpen = useStoreState((state) => state.isDrawerMenuOpen)

    const setDrawerOpen = useStoreActions((state) => state.setDrawerOpen)
    const setActiveTab = useStoreActions((state) => state.setActiveTab)
    const setPGNHeader = useStoreActions((state) => state.setPGNHeader)
    const setAnalysisGameTree = useStoreActions((state) => state.analysisMode.setGameTree)
    const setIsDrawerMenuOpen = useStoreActions((state) => state.setIsDrawerMenuOpen)
    const setSnackbarMessage = useStoreActions((state) => state.setSnackbarMessage)
    const gameState = useStoreState((state) => state.gameView.gameState)

    const { registerDialog } = useDialogs()
    const location = useLocation()
    const navigate = useNavigate()

    const clearQueryParams = (params: string[]) => {
        const searchParams = new URLSearchParams(location.search)
        params.forEach((param) => searchParams.delete(param))
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
    }

    const redirectToDrawer = (param: DrawerTypes) => {
        let active = activeTab
        let menuOpen = isDrawerMenuOpen
        switch (param) {
            case DrawerTypes.PLAYERS:
                active = 1
                menuOpen = false
                break
            case DrawerTypes.BOTS:
                active = 2
                menuOpen = false
                break
            case DrawerTypes.MENU:
                menuOpen = true
                break
            case DrawerTypes.GAME:
                menuOpen = false
                active = 0
                break
            default:
                break
        }
        setActiveTab(active)
        setIsDrawerMenuOpen(menuOpen)
        setDrawerOpen(gameState === GameViewState.GAME_RUNNING ? 'closed' : 'open')
        navigate('/')
    }

    const redirectToAnalysisWithPGN = (pgn: string) => {
        let decodedPGN: string
        let result: GameTree
        try {
            decodedPGN = decodeURIComponent(pgn)
            result = gameFromPGN(decodedPGN)
        } catch (e) {
            setSnackbarMessage('Invalid PGN')
            return
        }
        let headers = pgnParseHeaders(decodedPGN)
        if (headers.length > 0) {
            setPGNHeader({
                Event: '-',
                Site: '-',
                Date: '',
                Round: '-',
                White: '',
                Black: '',
                Result: '*',
                ...headers[0],
            })
            setAnalysisGameTree(result)
            navigate('/analysis')
        }
    }

    const showOverlay = (overlay: Dialogs) => {
        const overlayComponents: Record<string, JSX.Element> = {
            SIGNUP: <SignUp />,
            SIGNIN: <SignIn />,
            FORGOT_PASSWORD: <ForgotPassword />,
            SETTINGS: <SettingsDialog />,
        }
        if (!isEmpty(overlay) && overlayComponents[overlay]) {
            registerDialog(Dialogs[overlay], overlayComponents[overlay])
            clearQueryParams(['overlay'])
        }
    }

    const showResetPassword = (params: URLSearchParams) => {
        const newPasswordTokens = params.getAll(DeepLinkTypes.TOKEN)
        const token = newPasswordTokens[0]
        if (token) {
            registerDialog(Dialogs.RESET_PASSWORD, <ResetPassword newPasswordToken={token} />)
            clearQueryParams([DeepLinkTypes.RESET_PASSWORD, DeepLinkTypes.TOKEN])
        }
    }

    useEffect(() => {
        let searchParams = ''
        if (location.search == '' && location.hash !== '') {
            searchParams = location.hash.replace('#/', '')
        } else {
            searchParams = location.search
        }
        const params = new URLSearchParams(searchParams)

        deepLinkTypesValues.map((type) => {
            const paramsData = params.getAll(type)
            const param = paramsData[0]
            if (param) {
                switch (type) {
                    case DeepLinkTypes.SYSTEM:
                        useConfig(param, API_URL)
                        break
                    case DeepLinkTypes.DRAWER:
                        redirectToDrawer(param.toLowerCase() as DrawerTypes)
                        break
                    case DeepLinkTypes.PGN:
                        redirectToAnalysisWithPGN(param)
                        break
                    case DeepLinkTypes.OVERLAY:
                        showOverlay(param.toLocaleUpperCase() as Dialogs)
                        break
                    case DeepLinkTypes.RESET_PASSWORD:
                        showResetPassword(params)
                        break
                    case DeepLinkTypes.TOKEN:
                    default:
                        break
                }
            }
        })
    }, [location.search])

    return null
}

DeepLinksService.displayName = 'DeepLinksService'

export default DeepLinksService
